export interface HNItem {
  id: number;
  deleted?: boolean;
  type: "job" | "story" | "comment" | "poll" | "pollopt";
  by?: string;
  time: number;
  text?: string;
  dead?: boolean;
  parent?: number;
  poll?: number;
  kids?: number[];
  url?: string;
  score?: number;
  title?: string;
  parts?: number[];
  descendants?: number;
}

const BASE_URL = "https://hacker-news.firebaseio.com/v0";
const FETCH_TIMEOUT_MS = 8000;
const MAX_RETRIES = 2;

// Simple in-memory cache to prevent redundant fetches for the same item ID
// and to coalesce concurrent requests for the same item.
const itemCache = new Map<number, Promise<HNItem>>();
const MAX_CACHE_SIZE = 1000;

/** Fetch with an AbortController-based timeout */
async function fetchWithTimeout(url: string, timeoutMs = FETCH_TIMEOUT_MS): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: controller.signal });
    return res;
  } finally {
    clearTimeout(timer);
  }
}

/** Fetch with retry for transient failures */
async function fetchWithRetry(url: string, retries = MAX_RETRIES): Promise<Response> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetchWithTimeout(url);
      if (res.ok) return res;
      // Don't retry 4xx errors
      if (res.status >= 400 && res.status < 500) return res;
    } catch (err) {
      if (attempt === retries) throw err;
    }
    // Exponential backoff: 500ms, 1000ms
    await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
  }
  // Should not reach here, but TypeScript needs it
  throw new Error(`Failed to fetch ${url} after ${retries + 1} attempts`);
}

export async function fetchFeed(type: string = "topstories"): Promise<number[]> {
  const res = await fetchWithRetry(`${BASE_URL}/${type}.json`);
  if (!res.ok) throw new Error(`Failed to fetch ${type}`);
  return res.json();
}

export async function fetchItem(id: number, clearCache = false): Promise<HNItem> {
  if (clearCache) {
    itemCache.delete(id);
  }
  
  if (itemCache.has(id)) {
    return itemCache.get(id)!;
  }

  const promise = fetchWithRetry(`${BASE_URL}/item/${id}.json`).then(async (res) => {
    if (!res.ok) throw new Error(`Failed to fetch item ${id}`);
    return res.json();
  }).catch((err) => {
    itemCache.delete(id); // Remove failed fetch from cache
    throw err;
  });

  if (itemCache.size >= MAX_CACHE_SIZE) {
    // Very simple eviction: remove the first added item
    const firstKey = itemCache.keys().next().value;
    if (firstKey !== undefined) {
      itemCache.delete(firstKey);
    }
  }

  itemCache.set(id, promise);
  return promise;
}

// Function to clear the entire cache (for testing purposes)
export function clearItemCache(): void {
  itemCache.clear();
}

export async function fetchItems(ids: number[]): Promise<HNItem[]> {
  const CHUNK_SIZE = 10;
  const items: HNItem[] = [];

  for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
    const chunkIds = ids.slice(i, i + CHUNK_SIZE);
    const chunkPromises = chunkIds.map((id) => fetchItem(id));
    const chunkItems = await Promise.all(chunkPromises);
    items.push(...chunkItems);
  }

  return items;
}

